/**
 * Nommage des fichiers telecharges depuis le back-office.
 *
 * Retour du 28/08 : un meme document ne portait pas le meme nom selon l'ecran
 * d'ou on le telechargeait. « Dernier RDV » produisait la syntaxe attendue,
 * « Secretariat (dossier) » une autre — dans le mauvais ordre, sans le numero
 * de dossier, et surtout en ignorant le nom d'usage : un patient qui n'a
 * qu'un nom d'usage ressortait avec un nom vide.
 *
 * La regle est celle de « Dernier RDV », et elle vit desormais ici seulement.
 * Les deux ecrans appellent ces fonctions : il n'est plus possible qu'elles
 * divergent a nouveau.
 */

/** Trois premieres lettres, sans espaces ni accents parasites. */
export function initiales(valeur, longueur = 3) {
  return String(valeur || "")
    .toUpperCase()
    .replace(/\s/g, "")
    .slice(0, longueur);
}

/**
 * Le nom d'usage prime, le nom de naissance sert de repli.
 * C'est exactement l'inverse de ce que faisait l'ecran secretariat.
 */
export function nomRetenu({ maritalName, lastNameUsed, lastName, lastNameBirth } = {}) {
  return maritalName || lastNameUsed || lastName || lastNameBirth || "";
}

/** Prefixe « numero de dossier » quand il est renseigne, nettoye. */
export function prefixeDossier(barcode, separateur) {
  const v = String(barcode || "").replace(/[^A-Za-z0-9-]/g, "");
  return v ? `${v}${separateur}` : "";
}

/**
 * Type de document tel qu'il apparait dans le nom de fichier.
 * Les libelles affiches a l'ecran (« Piece d'identite ») ne conviennent pas :
 * ce sont ces jetons courts qui sont attendus dans le nom.
 */
export const TYPE_FICHIER_PAR_CLE = Object.freeze({
  prescription: "ordo1",
  iaPrescription: "ordo1",
  prescriptiontwo: "ordo2",
  prescriptionthree: "ordo3",
  prescriptionfour: "ordo4",
  mutual: "mutuelle",
  IDCard: "piece-identite",
  vitalCard: "carte-vitale",
  consent: "consentement",
  document: "document",
});

/**
 * Nom d'un document image telecharge.
 * Forme : [dossier-]AAAA-MM-JJ-type-PRE-NOM.jpg
 */
export function nomFichierDocument({
  barcode,
  jour,
  type,
  firstName,
  maritalName,
  lastName,
  lastNameUsed,
  lastNameBirth,
  extension = "jpg",
}) {
  const dossier = prefixeDossier(barcode, "-");
  const pre3 = initiales(firstName);
  const nom3 = initiales(
    nomRetenu({ maritalName, lastNameUsed, lastName, lastNameBirth })
  );
  return `${dossier}${jour}-${type}-${pre3}-${nom3}.${extension}`;
}

/**
 * Nom du PDF de resume d'un rendez-vous.
 * Forme : [dossier_]JJ_MM_AAAA_PRE_NOM.pdf
 */
export function nomFichierResume({
  barcode,
  date,
  firstName,
  maritalName,
  lastName,
  lastNameUsed,
  lastNameBirth,
}) {
  const dossier = prefixeDossier(barcode, "_");
  const pre3 = initiales(firstName);
  const nom3 = initiales(
    nomRetenu({ maritalName, lastNameUsed, lastName, lastNameBirth })
  );
  return `${dossier}${date}_${pre3}_${nom3}.pdf`;
}

export default {
  initiales,
  nomRetenu,
  prefixeDossier,
  nomFichierDocument,
  nomFichierResume,
  TYPE_FICHIER_PAR_CLE,
};
